import { NacelleConnector, NacelleConnectorParams } from '../nacelleConnector'
import {
  FetchProductParams,
  FetchProductsParams,
  FetchCollectionParams,
  FetchCollectionPageParams,
  FetchContentParams,
  FetchPageParams,
  FetchPagesParams,
  FetchBlogParams,
  FetchArticleParams,
  FetchArticlesParams,
  FetchBlogPageParams
} from '../interfaces/parameters'
import { Product, ProductOptions, createProduct } from '../../objects/product'
import {
  createCollection,
  Collection,
  CreateCollectionOptions
} from '../../objects/collection'
import {
  createContent,
  Content,
  CreateContentOptions
} from '../../objects/content'
import constructDataPath from './utils/constructDataPath'
import getListHandles from './utils/getListHandles'
import { Space, CreateSpaceOptions, createSpace } from '../../objects/space'

export interface NacelleStaticConnectorParams extends NacelleConnectorParams {
  basePath?: string
}

export interface NacellStaticRequestResponse<T> {
  success: boolean
  data: T | null
  error?: string
}

export interface SearchStaticResponse {
  products: Product[]
  [key: string]: any
}

interface OnErrorParams {
  method: string
  params: any
  error: Error
}

interface OnError {
  <T>(options: OnErrorParams): Promise<T> | T
}

export default class NacelleStaticConnector extends NacelleConnector {
  basePath: string
  onError?: OnError

  constructor(params?: NacelleStaticConnectorParams) {
    super(params)

    this.basePath = params && params.basePath ? params.basePath : '/'
  }

  async request<T>(path: string): Promise<NacellStaticRequestResponse<T>> {
    const url = `${this.basePath}${path}`

    try {
      const response = await this.apiClient.get<T>(url)

      if (
        response &&
        response.data &&
        response.headers &&
        response.headers['content-type'].includes('application/json')
      ) {
        return {
          success: true,
          data: response.data
        }
      }

      throw new Error(`Unable to find static resource at ${url}`)
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: null
      }
    }
  }

  async requestMany<T>(paths: string[]): Promise<T[]> {
    const requests = paths.map(path => {
      return this.request<T>(path)
    })
    const responses = await Promise.all(requests)
    const successes = responses.reduce((arr, response) => {
      if (response.success && response.data) {
        arr.push(response.data)
      }

      return arr
    }, [] as T[])

    return successes
  }

  async product(options: FetchProductParams): Promise<Product> {
    try {
      const { handle, locale } = options
      const useLocale = locale || this.locale
      const path = constructDataPath({
        type: 'product',
        handle: handle,
        locale: useLocale
      })
      const response = await this.request<ProductOptions>(path)

      if (response.success && response.data) {
        return createProduct(response.data)
      }

      throw new Error(response.error)
    } catch (error) {
      if (this.onError) {
        return this.onError<Product>({
          method: 'product',
          params: options,
          error
        })
      }
      throw error
    }
  }

  async products(options: FetchProductsParams): Promise<Product[]> {
    const { handles, locale } = options
    const useLocale = locale || this.locale

    const requests = handles.map(handle => {
      return this.product({
        handle,
        locale: useLocale
      }).catch(() => {
        return undefined
      })
    })

    const responses = await Promise.all(requests)

    const successes = responses.reduce((arr, response) => {
      if (response) {
        arr.push(response)
      }

      return arr
    }, [] as Product[])

    return successes
  }

  async collection(options: FetchCollectionParams): Promise<Collection> {
    try {
      const { handle, locale } = options
      const useLocale = locale || this.locale
      const path = constructDataPath({
        type: 'collection',
        handle: handle,
        locale: useLocale
      })
      const response = await this.request<CreateCollectionOptions>(path)

      if (response.success && response.data) {
        return createCollection(response.data)
      }

      throw new Error(response.error)
    } catch (error) {
      if (this.onError) {
        return this.onError<Collection>({
          method: 'collection',
          params: options,
          error
        })
      }

      throw error
    }
  }

  async collectionPage(options: FetchCollectionPageParams): Promise<Product[]> {
    try {
      const { handle, locale, list, paginate, index, itemsPerPage } = options
      let { collection } = options

      if (typeof collection === 'undefined') {
        if (handle && handle.length > 0) {
          collection = await this.collection({
            handle,
            locale
          })
        } else {
          throw new Error('A collection or handle is required')
        }
      }

      const handles = getListHandles(collection.productLists, {
        selectedList: list,
        paginate: typeof paginate !== 'undefined' ? paginate : false,
        index,
        itemsPerPage
      })

      return this.products({
        handles,
        locale
      })
    } catch (error) {
      if (this.onError) {
        return this.onError<Product[]>({
          method: 'collectionPage',
          params: options,
          error
        })
      }

      throw error
    }
  }

  async content({
    handle,
    type = 'page',
    locale,
    blogHandle = 'blog'
  }: FetchContentParams): Promise<Content> {
    try {
      const useLocale = locale || this.locale
      let path = constructDataPath({
        type,
        handle: handle,
        locale: useLocale
      })

      if (type === 'article') {
        path = constructDataPath({
          type,
          handle: handle,
          locale: useLocale,
          parentHandle: blogHandle
        })
      }

      const response = await this.request<CreateContentOptions>(path)

      if (response.success && response.data) {
        return createContent(response.data)
      }

      throw new Error(response.error)
    } catch (error) {
      if (this.onError) {
        return this.onError<Content>({
          method: 'content',
          params: { handle, type, locale, blogHandle },
          error
        })
      }

      throw error
    }
  }

  page(options: FetchPageParams): Promise<Content> {
    return this.content({
      handle: options.handle,
      type: 'page',
      locale: options.locale
    })
  }

  async pages(options: FetchPagesParams): Promise<Content[]> {
    const { handles, locale } = options
    const useLocale = locale || this.locale

    const requests = handles.map(handle => {
      return this.content({
        type: 'page',
        handle,
        locale: useLocale
      }).catch(() => {
        return undefined
      })
    })

    const responses = await Promise.all(requests)

    const successes = responses.reduce((arr, response) => {
      if (response) {
        arr.push(response)
      }

      return arr
    }, [] as Content[])

    return successes
  }

  blog(options: FetchBlogParams): Promise<Content> {
    return this.content({
      handle: options.handle,
      type: 'blog',
      locale: options.locale
    })
  }

  article(options: FetchArticleParams): Promise<Content> {
    return this.content({
      handle: options.handle,
      type: 'article',
      locale: options.locale,
      blogHandle: options.blogHandle || 'blog'
    })
  }

  async articles(options: FetchArticlesParams): Promise<Content[]> {
    const { handles, locale, blogHandle } = options
    const useLocale = locale || this.locale

    const requests = handles.map(handle => {
      return this.article({
        handle,
        blogHandle,
        locale: useLocale
      }).catch(() => {
        return undefined
      })
    })

    const responses = await Promise.all(requests)

    const successes = responses.reduce((arr, response) => {
      if (response) {
        arr.push(response)
      }

      return arr
    }, [] as Content[])

    return successes
  }

  async blogPage(options: FetchBlogPageParams): Promise<Content[]> {
    try {
      const { handle, locale, list, paginate, index, itemsPerPage } = options
      let { blog } = options

      if (typeof blog === 'undefined') {
        if (handle && handle.length > 0) {
          blog = await this.blog({
            handle,
            locale
          })
        } else {
          throw new Error('A blog or handle is required')
        }
      }

      if (blog.articleLists) {
        const handles = getListHandles(blog.articleLists, {
          selectedList: list,
          paginate: typeof paginate !== 'undefined' ? paginate : false,
          index,
          itemsPerPage
        })

        return this.articles({
          handles,
          locale,
          blogHandle: handle || blog.handle
        })
      }

      return []
    } catch (error) {
      if (this.onError) {
        return this.onError<Content[]>({
          method: 'blogPage',
          params: options,
          error
        })
      }

      throw error
    }
  }

  async space(): Promise<Space> {
    const path = 'data/space.json'
    const response = await this.request<CreateSpaceOptions>(path)

    if (response.success && response.data) {
      return createSpace(response.data)
    }

    throw new Error(response.error)
  }

  async allProducts(): Promise<Product[]> {
    const path = 'data/search.json'
    const response = await this.request<SearchStaticResponse>(path)

    if (response.success && response.data && response.data.products) {
      return response.data.products.map(product => createProduct(product))
    }

    if (response.error) {
      throw new Error(response.error)
    }

    throw new Error('Unable to get all products from search.json static file.')
  }
}
